import { useEffect, useState, useMemo } from "react";
import { collection, onSnapshot, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { FaUserMinus, FaExternalLinkAlt, FaCheck, FaTrash } from "react-icons/fa";
import "./AccountDeleteRequests.css";

const formatRequestDate = (value) => {
  if (!value) return "N/A";
  if (typeof value === "string") return value;
  if (value._seconds != null) {
    return new Date(value._seconds * 1000).toLocaleString(undefined, {
      dateStyle: "long",
      timeStyle: "medium",
    });
  }
  if (value.toMillis) {
    return new Date(value.toMillis()).toLocaleString(undefined, {
      dateStyle: "long",
      timeStyle: "medium",
    });
  }
  if (value instanceof Date) return value.toLocaleString();
  return "N/A";
};

const AccountDeleteRequests = () => {
  const [accountDeletionList, setAccountDeletionList] = useState([]);
  const [deletionList, setDeletionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | "accountDeletionRequests" | "deletionRequests"
  const [sortBy, setSortBy] = useState("newestFirst");
  const [loadedSources, setLoadedSources] = useState({ account: false, deletion: false });
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (loadedSources.account && loadedSources.deletion) {
      setLoading(false);
    }
  }, [loadedSources.account, loadedSources.deletion]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setLoadedSources({ account: false, deletion: false });

    const accountRef = collection(db, "accountDeletionRequests");
    const deletionRef = collection(db, "deletionRequests");

    const unsubAccount = onSnapshot(
      accountRef,
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          const d = doc.data();
          list.push({
            id: doc.id,
            source: "accountDeletionRequests",
            authId: d.authId ?? "",
            reason: d.reason ?? "",
            requestDate: d.requestDate ?? null,
            status: d.status ?? "",
            type: d.userType ?? d.type ?? "",
            name: null,
            email: null,
            phone: null,
          });
        });
        setAccountDeletionList(list);
        setLoadedSources((prev) => ({ ...prev, account: true }));
      },
      (err) => {
        console.error("Error listening to accountDeletionRequests:", err);
        setError(err.message);
        setLoadedSources((prev) => ({ ...prev, account: true }));
      }
    );

    const unsubDeletion = onSnapshot(
      deletionRef,
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          const d = doc.data();
          const userData = d.userData || {};
          list.push({
            id: doc.id,
            source: "deletionRequests",
            authId: d.authId ?? "",
            reason: d.reason ?? "",
            requestDate: d.requestedAt ?? null,
            status: d.status ?? "",
            type: userData.userType ?? d.userType ?? d.type ?? "",
            name: userData.name ?? null,
            email: userData.email ?? null,
            phone: userData.phone ?? null,
          });
        });
        setDeletionList(list);
        setLoadedSources((prev) => ({ ...prev, deletion: true }));
      },
      (err) => {
        console.error("Error listening to deletionRequests:", err);
        setError(err.message);
        setLoadedSources((prev) => ({ ...prev, deletion: true }));
      }
    );

    return () => {
      unsubAccount();
      unsubDeletion();
    };
  }, []);

  const allRequests = useMemo(() => {
    const combined = [
      ...accountDeletionList.map((r) => ({ ...r })),
      ...deletionList.map((r) => ({ ...r })),
    ];
    return combined;
  }, [accountDeletionList, deletionList]);

  const filteredAndSorted = useMemo(() => {
    let list = allRequests;

    if (sourceFilter === "accountDeletionRequests") {
      list = list.filter((r) => r.source === "accountDeletionRequests");
    } else if (sourceFilter === "deletionRequests") {
      list = list.filter((r) => r.source === "deletionRequests");
    }

    if (statusFilter) {
      list = list.filter((r) => (r.status || "").toLowerCase() === statusFilter.toLowerCase());
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          (r.authId && r.authId.toLowerCase().includes(term)) ||
          (r.reason && r.reason.toLowerCase().includes(term)) ||
          (r.type && r.type.toLowerCase().includes(term)) ||
          (r.name && r.name.toLowerCase().includes(term)) ||
          (r.email && r.email.toLowerCase().includes(term)) ||
          (r.phone && String(r.phone).includes(term))
      );
    }

    list = [...list].sort((a, b) => {
      const aTime = getSortTime(a.requestDate);
      const bTime = getSortTime(b.requestDate);
      return sortBy === "newestFirst" ? bTime - aTime : aTime - bTime;
    });

    return list;
  }, [allRequests, sourceFilter, statusFilter, searchTerm, sortBy]);

  const getCollectionName = (source) =>
    source === "deletionRequests" ? "deletionRequests" : "accountDeletionRequests";

  const handleMarkAsSolved = async (req) => {
    if (updatingId) return;
    try {
      setUpdatingId(req.id);
      const col = getCollectionName(req.source);
      const docRef = doc(db, col, req.id);
      await updateDoc(docRef, { status: "solved" });
    } catch (err) {
      console.error("Error marking request as solved:", err);
      alert("Failed to mark as solved. " + (err.message || String(err)));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteRequest = async (req) => {
    if (deletingId) return;
    if (!window.confirm("Are you sure you want to delete this request? This cannot be undone.")) return;
    try {
      setDeletingId(req.id);
      const col = getCollectionName(req.source);
      const docRef = doc(db, col, req.id);
      await deleteDoc(docRef);
    } catch (err) {
      console.error("Error deleting request:", err);
      alert("Failed to delete request. " + (err.message || String(err)));
    } finally {
      setDeletingId(null);
    }
  };

  function getSortTime(requestDate) {
    if (!requestDate) return 0;
    if (requestDate._seconds != null) return requestDate._seconds * 1000;
    if (requestDate.toMillis) return requestDate.toMillis();
    if (requestDate instanceof Date) return requestDate.getTime();
    const parsed = new Date(requestDate);
    return isNaN(parsed) ? 0 : parsed.getTime();
  }

  if (loading && allRequests.length === 0) {
    return (
      <div className="account-delete-requests-container">
        <div className="loading-container">
          <span className="loader"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="account-delete-requests-container">
      <div className="header">
        <h2>
          <FaUserMinus className="header-icon" /> Account Delete Requests
        </h2>
      </div>

      <div className="controls-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by authId, reason, type, name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-container">
          <label htmlFor="sourceFilter">Source:</label>
          <select
            id="sourceFilter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All sources</option>
            <option value="accountDeletionRequests">accountDeletionRequests</option>
            <option value="deletionRequests">deletionRequests</option>
          </select>
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="pending">Pending</option>
            <option value="solved">Solved</option>
          </select>
          <label htmlFor="sortBy">Sort:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="newestFirst">Newest first</option>
            <option value="oldestFirst">Oldest first</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          Error: {error}. Data may be partial.
        </div>
      )}

      <div className="summary">
        Showing <strong>{filteredAndSorted.length}</strong> of{" "}
        <strong>{allRequests.length}</strong> requests
        {allRequests.length > 0 && (
          <span className="source-breakdown">
            (accountDeletionRequests: {accountDeletionList.length}, deletionRequests:{" "}
            {deletionList.length})
          </span>
        )}
      </div>

      {filteredAndSorted.length === 0 ? (
        <div className="empty-container">
          <p>No account delete requests found.</p>
        </div>
      ) : (
        <div className="requests-grid">
          {filteredAndSorted.map((req) => (
            <div key={`${req.source}-${req.id}`} className="request-card">
              <div className="request-header">
                <span className="request-auth-id" title={req.authId}>
                  {req.authId ? `${req.authId.slice(0, 12)}...` : "—"}
                </span>
                <span className={`status-badge status-${(req.status || "").toLowerCase()}`}>
                  {req.status || "—"}
                </span>
                <span className="source-badge" title={req.source}>
                  {req.source === "deletionRequests" ? "deletionRequests" : "accountDeletion"}
                </span>
              </div>
              <div className="request-body">
                <div className="request-field">
                  <span className="field-label">Reason</span>
                  <span className="field-value reason">{req.reason || "—"}</span>
                </div>
                <div className="request-field">
                  <span className="field-label">Request date</span>
                  <span className="field-value">{formatRequestDate(req.requestDate)}</span>
                </div>
                <div className="request-field">
                  <span className="field-label">Type</span>
                  <span className="field-value">{req.type || "—"}</span>
                </div>
                {(req.name || req.email || req.phone) && (
                  <>
                    {req.name && (
                      <div className="request-field">
                        <span className="field-label">Name</span>
                        <span className="field-value">{req.name}</span>
                      </div>
                    )}
                    {req.email && (
                      <div className="request-field">
                        <span className="field-label">Email</span>
                        <span className="field-value">{req.email}</span>
                      </div>
                    )}
                    {req.phone && (
                      <div className="request-field">
                        <span className="field-label">Phone</span>
                        <span className="field-value">{req.phone}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="request-field full-auth">
                  <span className="field-label">Auth ID</span>
                  <span className="field-value monospace" title={req.authId}>
                    {req.authId || "—"}
                  </span>
                </div>
              </div>
              <div className="request-card-actions">
                <button
                  type="button"
                  className="card-action-btn show-account-btn"
                  onClick={async () => {
                    const typeLower = (req.type || "").toLowerCase();
                    const isServiceman =
                      typeLower === "servicemen" || typeLower === "serviceman";
                    const path = isServiceman ? "/servicemen" : "/customers";
                    let phone = (req.phone || "").toString().trim();
                    const authId = (req.authId || "").toString().trim();

                    if (!phone && authId) {
                      try {
                        const collectionName = isServiceman ? "servicemen" : "users";
                        const docRef = doc(db, collectionName, authId);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                          const data = docSnap.data();
                          phone = (data.phone ?? data.Phone ?? "").toString().trim();
                        }
                      } catch (err) {
                        console.error("Error fetching user/serviceman by authId:", err);
                      }
                    }

                    const params = new URLSearchParams();
                    if (phone) {
                      params.set("phone", phone);
                    } else if (authId) {
                      params.set("id", authId);
                    }
                    const query = params.toString();
                    const url = query
                      ? `${window.location.origin}${path}?${query}`
                      : `${window.location.origin}${path}`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  title="Open Customers or Servicemen list with this account"
                >
                  <FaExternalLinkAlt /> Show Account
                </button>
                <button
                  type="button"
                  className="card-action-btn mark-solved-btn"
                  onClick={() => handleMarkAsSolved(req)}
                  disabled={updatingId !== null || (req.status || "").toLowerCase() === "solved"}
                  title="Mark this request as solved"
                >
                  <FaCheck /> {updatingId === req.id ? "..." : "Mark as solved"}
                </button>
                <button
                  type="button"
                  className="card-action-btn delete-request-btn"
                  onClick={() => handleDeleteRequest(req)}
                  disabled={deletingId !== null}
                  title="Delete this request permanently"
                >
                  <FaTrash /> {deletingId === req.id ? "..." : "Delete this request"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountDeleteRequests;
